import { useState, useCallback } from 'react';
import * as provisioningApi from '../services/api/provisioningApi';

/**
 * Maps HTTP error responses to user-friendly messages.
 */
const getErrorMessage = (err) => {
  const status = err.response?.status;
  const serverMessage = err.response?.data?.message;

  if (serverMessage) return serverMessage;

  switch (status) {
    case 400: return 'Invalid input. Please check your entries and try again.';
    case 403: return 'You do not have permission to perform this action.';
    case 409: return 'A conflict was detected. The record may already exist.';
    case 502: return 'Unable to connect to an external database. You may enter values manually.';
    case 503: return 'A required service is temporarily unavailable. Please try again later.';
    case 504: return 'The operation timed out. Please try again.';
    default: return 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Custom hook that wraps provisioning API calls with loading states and error handling.
 * Provides methods for each wizard step action.
 */
export const useProvisioningApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Generic wrapper for API calls with loading/error state management
   */
  const execute = useCallback(async (apiCall) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall();
      return response.data;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Step 1: Customer Creation
  const createCustomer = useCallback(async (sessionId, customerData) => {
    return execute(() => provisioningApi.createCustomer(sessionId, customerData));
  }, [execute]);

  // Step 2: Ruleset Copy
  const copyRulesets = useCallback(async (sessionId, data) => {
    return execute(() => provisioningApi.copyRulesets(sessionId, data));
  }, [execute]);

  // Step 2: Reference Customer Check
  const checkReferenceCustomer = useCallback(async (sessionId, referenceCustomerId) => {
    return execute(() => provisioningApi.checkReferenceCustomer(sessionId, referenceCustomerId));
  }, [execute]);

  // Step 3: Metadata Fields
  const createMetadataFields = useCallback(async (sessionId) => {
    return execute(() => provisioningApi.createMetadataFields(sessionId));
  }, [execute]);

  // Step 4: Delivery Configuration
  const createDeliveryConfig = useCallback(async (sessionId, configData) => {
    return execute(() => provisioningApi.createDeliveryConfig(sessionId, configData));
  }, [execute]);

  // Step 5: BFE Search
  const searchBfeJobs = useCallback(async (sessionId, searchTerm) => {
    return execute(() => provisioningApi.searchBfeJobs(sessionId, searchTerm));
  }, [execute]);

  // Step 6: XML Schema
  const parseXmlSchema = useCallback(async (sessionId, schemaData) => {
    return execute(() => provisioningApi.parseXmlSchema(sessionId, schemaData));
  }, [execute]);

  // Step 6: Field keys for autocomplete
  const getFieldKeys = useCallback(async (sessionId, rulesetId) => {
    return execute(() => provisioningApi.getFieldKeys(sessionId, rulesetId));
  }, [execute]);

  // Step 7: Job Creation
  const createJobs = useCallback(async (sessionId, jobData) => {
    return execute(() => provisioningApi.createJobs(sessionId, jobData));
  }, [execute]);

  // Step 8: Validation
  const validateProvisioning = useCallback(async (sessionId) => {
    return execute(() => provisioningApi.validateProvisioning(sessionId));
  }, [execute]);

  // Step 9: Enable
  const enableCustomer = useCallback(async (sessionId) => {
    return execute(() => provisioningApi.enableCustomer(sessionId));
  }, [execute]);

  // Delivery defaults
  const getDeliveryDefaults = useCallback(async () => {
    return execute(() => provisioningApi.getDeliveryDefaults());
  }, [execute]);

  // Ipsnet search (pipeline job IDs)
  const searchIpsnetJobs = useCallback(async (sessionId, searchTerm) => {
    return execute(() => provisioningApi.searchIpsnetJobs(sessionId, searchTerm));
  }, [execute]);

  return {
    loading,
    error,
    clearError: () => setError(null),

    // Step actions
    createCustomer,
    copyRulesets,
    checkReferenceCustomer,
    createMetadataFields,
    createDeliveryConfig,
    searchBfeJobs,
    searchIpsnetJobs,
    getDeliveryDefaults,
    parseXmlSchema,
    getFieldKeys,
    createJobs,
    validateProvisioning,
    enableCustomer
  };
};
